import { useInfiniteQuery } from '@tanstack/react-query';

import { client } from '../common';
import type { Car } from './types';

type Variables = {
  paginate: boolean;
  address: string;
  boundingBox: {
    topRight: {
      lat: number;
      lng: number;
    };
    bottomLeft: {
      lat: number;
      lng: number;
    };
  };
  startDatetime: string;
  endDatetime: string;
  deliveryAddress?: string;
  returnAddress?: string;
  sort: string;
  locationId?: number;
  latitude?: number;
  longitude?: number;
  models: number[];
  minDailyPrice?: number;
  maxDailyPrice?: number;
  autopilotTypes: number[];
  numSeats: number[];
  batteryRanges: number[];
  carIds: number[];
};

type CarsAvailablePage = {
  data: Car[];
  meta: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
};

// Utility to check if required params are defined
const areRequiredParamsDefined = (
  variables: Variables,
  requiredKeys: (keyof Variables)[]
) => requiredKeys.every((key) => variables[key] !== '');

const fetchCarsAvailable = async ({
  pageParam,
  variables,
}: {
  pageParam: number;
  variables: Variables;
}) => {
  try {
    // console.log('Making API call, pageParam:', pageParam);

    let params: Record<string, any> = {
      start_datetime: variables.startDatetime,
      end_datetime: variables.endDatetime,
      delivery_address: variables.deliveryAddress,
      return_address: variables.returnAddress,
      sort: variables.sort,
      models: variables.models,
      location_id: variables.locationId,
      min_daily_price: variables.minDailyPrice,
      max_daily_price: variables.maxDailyPrice,
      autopilot_types: variables.autopilotTypes,
      num_seats: variables.numSeats,
      battery_ranges: variables.batteryRanges,
      car_ids: variables.carIds,
    };

    if (variables.paginate) {
      params.page = pageParam;
    }

    if (variables.address) {
      params.address = variables.address;
    }

    if (variables.latitude !== undefined && variables.longitude !== undefined) {
      params.latitude = variables.latitude;
      params.longitude = variables.longitude;
    }

    if (variables.boundingBox) {
      params.bounding_box = {
        top_right: {
          lat: variables.boundingBox.topRight.lat,
          lng: variables.boundingBox.topRight.lng,
        },
        bottom_left: {
          lat: variables.boundingBox.bottomLeft.lat,
          lng: variables.boundingBox.bottomLeft.lng,
        },
      };
    }

    const response = await client.post('v2/cars/available', params);

    return response.data;
  } catch (error) {
    console.error('fetchCarsAvailable error:', error);
    throw error;
  }
};

export const useCarsAvailable = (variables: Variables) => {
  const requiredKeys: (keyof Variables)[] = ['startDatetime', 'endDatetime'];

  const shouldFetch =
    areRequiredParamsDefined(variables, requiredKeys) &&
    (variables.address !== '' ||
      (variables.deliveryAddress !== '' && variables.returnAddress !== ''));

  return useInfiniteQuery({
    queryKey: ['cars/available', variables],
    queryFn: ({ pageParam = 1 }) =>
      fetchCarsAvailable({
        pageParam,
        variables,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: CarsAvailablePage) => {
      if (variables.paginate === false) {
        return undefined;
      }

      if (lastPage.meta.page >= lastPage.meta.totalPages) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled: shouldFetch,
  });
};
